import { useState } from "react";
import { Link } from "react-router-dom";
import { PUBLIC_BRAND } from "../api/brand";
import { useBrandStatus } from "./BrandStatus";
import { useAuth } from "../context/AuthContext";

export function PreviewBanner() {
  const { config } = useAuth();
  if (!config?.is_preview) return null;
  return (
    <div
      data-testid="preview-env-banner"
      className="sticky top-0 z-50 flex h-6 w-full items-center justify-center gap-2 border-b border-amber-400/30 bg-[rgb(var(--c-banner)/0.88)] text-center overflow-hidden whitespace-nowrap px-2 text-[9.5px] font-semibold uppercase tracking-[0.04em] text-amber-200 backdrop-blur-xl sm:text-[11px] sm:tracking-[0.18em]"
    >
      <span aria-hidden className="h-1.5 w-1.5 rounded-full bg-amber-400" />Preview environment · demo data present · not production
    </div>
  );
}

const PUB = process.env.PUBLIC_URL;
const BADGE = `${PUB}/brand/logo-badge.png`;
const BADGE_2X = `${PUB}/brand/logo-badge@2x.png`;
const CLOCK = `${PUB}/brand/logo-clock.png`;
const CLOCK_2X = `${PUB}/brand/logo-clock@2x.png`;
const BADGE_H = { sm: "h-12", md: "h-[52px]", lg: "h-24 sm:h-[104px]", xl: "h-[120px] sm:h-[200px]", hero: "h-[150px] sm:h-[230px]" } as const;
const CLOCK_PX = { sm: 28, md: 34, lg: 60, xl: 88, hero: 100 } as const;
type BrandSize = keyof typeof BADGE_H;
const FX = "brand-fx shrink-0 select-none transition-[filter] duration-200 group-hover:[filter:drop-shadow(0_0_14px_rgba(255,29,0,0.35))]";

type Variant = "lockup" | "mark" | "compact";

// 480px-tall logo for decorative use: uploaded logo if present, else the built-in badge; null until the status is known.
export function useLogoSrc(): string | null {
  const { status } = useBrandStatus();
  if (!status) return null;
  return status.logo && status.logo_version ? `${PUBLIC_BRAND}/logo?h=480&fmt=webp&v=${status.logo_version}` : BADGE_2X;
}

export function LogoWatermark({ rail = false }: { rail?: boolean }) {
  const src = useLogoSrc();
  const [failed, setFailed] = useState(false);
  return (
    <div aria-hidden data-testid="sidebar-logo-watermark" className={`sidebar-wm ${rail ? "sidebar-wm-rail" : ""}`}>
      {src && <img src={failed ? BADGE_2X : src} alt="" draggable={false} onError={() => setFailed(true)} />}
    </div>
  );
}

// Uploaded App logo when one exists; built-in badge otherwise (kept invisible until the status is known, so no flash).
function Badge({ size }: { size: BrandSize }) {
  const { status } = useBrandStatus();
  const [failed, setFailed] = useState(false);
  const cls = `${BADGE_H[size]} w-auto ${FX}`;
  const v = status?.logo && status.logo_version;
  if (v && !failed) {
    const u = (h: number, f: string) => `${PUBLIC_BRAND}/logo?h=${h}&fmt=${f}&v=${v}`;
    return (
      <picture>
        <source type="image/webp" srcSet={`${u(240, "webp")} 1x, ${u(480, "webp")} 2x`} />
        <img src={u(240, "png")} srcSet={`${u(240, "png")} 1x, ${u(480, "png")} 2x`} alt="Gameday Time" draggable={false}
          data-testid="brand-wordmark" data-brand-source="uploaded" onError={() => setFailed(true)} className={cls} />
      </picture>
    );
  }
  return <img src={BADGE} srcSet={`${BADGE} 1x, ${BADGE_2X} 2x`} alt="Gameday Time" draggable={false} data-testid="brand-wordmark"
    data-brand-source="builtin" className={`${cls} ${status ? "" : "invisible"}`} />;
}

export function BrandLogo({ size = "md", variant = "lockup", to, className = "" }: { size?: BrandSize; variant?: Variant; to?: string; className?: string }) {
  const img = variant === "compact"
    ? <><img src={CLOCK} srcSet={`${CLOCK} 1x, ${CLOCK_2X} 2x`} width={38} height={38} alt="" draggable={false} className={FX} />
        <span data-testid="brand-wordmark" className="font-display text-lg font-bold uppercase leading-none tracking-[0.02em] text-white">Gameday<span className="text-signal"> Time</span></span></>
    : variant === "mark"
    ? <img src={CLOCK} srcSet={`${CLOCK} 1x, ${CLOCK_2X} 2x`} width={CLOCK_PX[size]} height={CLOCK_PX[size]} alt="Gameday Time"
        draggable={false} data-testid="brand-wordmark" className={FX} />
    : <Badge size={size} />;
  const cls = `group inline-flex items-center ${variant === "compact" ? "gap-2.5" : ""} ${className}`;
  return to ? <Link to={to} className={cls} aria-label="Gameday Time home">{img}</Link> : <span className={cls}>{img}</span>;
}

export function Wordmark({ to = "/", size = "md", variant = "lockup" }: { to?: string; size?: BrandSize; variant?: Variant }) {
  return <BrandLogo to={to} size={size} variant={variant} />;
}
